import React, { useState } from 'react'
import { formatDate } from './Utilities'

const inviteUrlFor = (invite_url) => invite_url

const usesLabel = (link) => `${link.use_count} / ${link.max_uses == null ? '∞' : link.max_uses}`

const expiryLabel = (link) => (link.expires_at ? formatDate(link.expires_at) : 'Never')

// Invite links section: create form + table of active links. The full
// redemption token is only ever returned in the create response (see
// dashboardSharingApi.createShareLink) — every other read gets
// `token_preview` only, so Copy is offered exclusively on the link just
// created in this session.
function DashboardInviteLinksPanel({ links, busy, error, justCreatedId, onCreate, onRevoke, onDelete, onCopy }) {
  const [role, setRole] = useState('editor')
  const [usageMode, setUsageMode] = useState('unlimited')
  const [expiryMode, setExpiryMode] = useState('default')
  const [expiryDays, setExpiryDays] = useState(30)

  const submitHandler = (event) => {
    event.preventDefault()
    const body = { role }
    body.max_uses = usageMode === 'single' ? 1 : null
    if (expiryMode === 'never') {
      body.expires_in_days = null
    } else if (expiryMode === 'custom') {
      body.expires_in_days = Number(expiryDays) || 30
    }
    // expiryMode === 'default' -> omit expires_in_days entirely, server defaults to 30 days
    onCreate(body)
  }

  return (
    <div className='dashboard-sharing-section' aria-label='Invite links'>
      <h3 className='dashboard-sharing-section-title'>Invite links</h3>
      <p className='dashboard-sharing-section-hint'>
        Anyone with an invite link can sign in (or create an account) and gain access at the role you choose.
      </p>

      <form className='dashboard-sharing-create-form' onSubmit={submitHandler}>
        <label className='dashboard-sharing-field'>
          <span>Role</span>
          <select value={role} onChange={e => setRole(e.target.value)}>
            <option value='editor'>Editor</option>
            <option value='viewer'>Viewer</option>
          </select>
        </label>
        <label className='dashboard-sharing-field'>
          <span>Uses</span>
          <select value={usageMode} onChange={e => setUsageMode(e.target.value)}>
            <option value='unlimited'>Unlimited uses</option>
            <option value='single'>Single use</option>
          </select>
        </label>
        <label className='dashboard-sharing-field'>
          <span>Expiry</span>
          <select value={expiryMode} onChange={e => setExpiryMode(e.target.value)}>
            <option value='default'>30 days (default)</option>
            <option value='custom'>Custom (days)</option>
            <option value='never'>Never expires</option>
          </select>
        </label>
        {expiryMode === 'custom' && (
          <label className='dashboard-sharing-field dashboard-sharing-field-narrow'>
            <span>Days</span>
            <input
              type='number'
              min={1}
              max={365}
              value={expiryDays}
              onChange={e => setExpiryDays(e.target.value)}
            />
          </label>
        )}
        <button type='submit' className='dashboard-sharing-button dashboard-sharing-button-primary' disabled={Boolean(busy)}>
          {busy === 'create-link' ? (
            <span className='dashboard-sharing-spinner' role='status' aria-label='Create link in progress' />
          ) : (
            <i className='fa-solid fa-link' aria-hidden='true' />
          )}
          <span>Create invite link</span>
        </button>
      </form>

      {error && (
        <div className='dashboard-sharing-inline-error' role='alert'>{error}</div>
      )}

      {links.length === 0 ? (
        <p className='dashboard-sharing-empty'>No active invite links yet.</p>
      ) : (
        <div className='dashboard-sharing-table-wrap'>
          <table className='dashboard-sharing-table'>
            <thead>
              <tr>
                <th>Created</th>
                <th>Role</th>
                <th>Uses</th>
                <th>Expires</th>
                <th aria-label='Actions' />
              </tr>
            </thead>
            <tbody>
              {links.map(link => (
                <tr key={link.id}>
                  <td>{formatDate(link.created_at)}</td>
                  <td className='dashboard-sharing-cell-capitalize'>{link.role}</td>
                  <td>{usesLabel(link)}</td>
                  <td>{expiryLabel(link)}</td>
                  <td className='dashboard-sharing-row-actions'>
                    {link.id === justCreatedId && link.invite_url && (
                      <button
                        type='button'
                        className='dashboard-sharing-row-btn'
                        onClick={() => onCopy(inviteUrlFor(link.invite_url))}
                        title='Copy invite link'
                      >
                        <i className='fa-solid fa-copy' aria-hidden='true' />
                        <span className='visually-hidden'>Copy invite link</span>
                      </button>
                    )}
                    <button
                      type='button'
                      className='dashboard-sharing-row-btn'
                      onClick={() => onRevoke(link.id)}
                      disabled={Boolean(busy)}
                      title='Revoke link'
                    >
                      <i className='fa-solid fa-ban' aria-hidden='true' />
                      <span className='visually-hidden'>Revoke</span>
                    </button>
                    <button
                      type='button'
                      className='dashboard-sharing-row-btn dashboard-sharing-row-btn-danger'
                      onClick={() => onDelete(link.id)}
                      disabled={Boolean(busy)}
                      title='Delete link'
                    >
                      <i className='fa-solid fa-trash' aria-hidden='true' />
                      <span className='visually-hidden'>Delete</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className='dashboard-sharing-section-hint'>
        The full link is shown once, right after you create it — after that only the last few characters are kept
        for recognition, so Copy only appears on a link you just created. Revoking or deleting a link does not
        remove people who already joined through it; use "People with access" below to remove them.
      </p>
    </div>
  )
}

export default DashboardInviteLinksPanel
